import { useMemo, useState } from "react";
import { useNavegacao } from "@/lib/navegacao";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { CardPracaHoje } from "@/components/mercado/card-praca-hoje";
import { StatusChip } from "@/components/mercado/status-chip";
import { useToast } from "@/hooks/use-toast";
import { useCarga, useRegistrarItem, useResolverProdutor } from "@/hooks/use-cargas";
import { fmtMoedaBR, fmtKgBR, fmtDataBR } from "@/lib/formato-mercado";
import { somarDiasCivis } from "@shared/data-fazenda";
import { PRODUTOS, chaveProduto } from "@shared/mercado-produtos";
import {
  ATALHOS_VENCIMENTO_ITEM, DIAS_VENCIMENTO_PADRAO_ITEM,
  type ItemRegistrado, type ProdutorCarteiraDTO,
} from "@shared/mercado-comprador";

/** Uma linha do formulário — `kg`/`preco` ficam como TEXTO até o envio. */
interface LinhaForm {
  produto: string;
  kg: string;
  preco: string;
}

const LINHA_VAZIA: LinhaForm = { produto: chaveProduto(PRODUTOS[0]), kg: "", preco: "" };

/**
 * Registrar produtor — o que faz a carga FECHAR.
 *
 * É esta tela que atende o produtor SEM conta no Rizoma, que é a maioria: uma
 * carga de 8 produtores em que 1 usa o app não é uma carga, é um romaneio com
 * ruído em volta, e o comprador voltaria para o caderno.
 *
 * ⚠️ O TOTAL É DO SERVIDOR. A prévia daqui é conferência visual do que foi
 * digitado; quem calcula o `valor` gravado é `valorDoRegistro`, e é o número do
 * servidor que o produtor vai conferir no link (PR B). Duas contas do mesmo
 * total divergem no primeiro centavo de arredondamento.
 *
 * ⚠️ A cotação da praça entra como REFERÊNCIA e nunca preenche o preço: o preço
 * é o que foi combinado com aquele produtor, e preencher o campo com a praça
 * transformaria uma referência num valor que ninguém acordou.
 */
export default function MercadoRegistrarProdutor({ cargaId }: { cargaId: string }) {
  const { toast } = useToast();
  const { voltar } = useNavegacao();
  const { data: carga, isLoading } = useCarga(cargaId);
  const registrar = useRegistrarItem(cargaId);

  const [nome, setNome] = useState("");
  const [contatoId, setContatoId] = useState<string | null>(null);
  const [telefone, setTelefone] = useState("");
  const [cidade, setCidade] = useState("");
  const [linhas, setLinhas] = useState<LinhaForm[]>([{ ...LINHA_VAZIA }]);
  const [dias, setDias] = useState<number | null>(DIAS_VENCIMENTO_PADRAO_ITEM);
  const [dataLivre, setDataLivre] = useState("");
  const [duplicata, setDuplicata] = useState<ProdutorCarteiraDTO[] | null>(null);

  // Autocomplete: só quando o comprador está digitando um nome NOVO.
  const { data: resolucao } = useResolverProdutor(contatoId ? "" : nome);

  const itens: ItemRegistrado[] = useMemo(
    () => linhas
      .filter(l => l.kg && l.preco)
      .map(l => {
        const produto = PRODUTOS.find(p => chaveProduto(p) === l.produto)!;
        return {
          variedade: produto.variedade,
          qualidade: produto.qualidade,
          kg: Number(l.kg.replace(",", ".")),
          precoKg: Number(l.preco.replace(",", ".")),
        };
      })
      .filter(i => Number.isFinite(i.kg) && i.kg > 0 && Number.isFinite(i.precoKg)),
    [linhas],
  );

  const totalKg = itens.reduce((a, i) => a + i.kg, 0);
  const totalValor = itens.reduce((a, i) => a + i.kg * i.precoKg, 0);

  const vencimento = dias != null && carga?.dataCarregamento
    ? somarDiasCivis(carga.dataCarregamento, dias)
    : (dataLivre || null);

  function enviar(criarNovo = false) {
    if (itens.length === 0) {
      toast({ title: "Falta o que ele carregou", description: "Informe pelo menos uma linha com kg e preço.", variant: "destructive" });
      return;
    }
    registrar.mutate(
      {
        produtor: contatoId
          ? { contatoId }
          : { nome: nome.trim(), telefone: telefone.trim() || null, cidade: cidade.trim() || null, criarNovo },
        itens,
        vencimento,
      },
      {
        onSuccess: (r) => {
          // A duplicata é PERGUNTA: nada foi gravado, e a tela pergunta antes de
          // criar o segundo cadastro do mesmo produtor.
          if (!r.ok) { setDuplicata(r.parecidos); return; }
          voltar(`/mercado/cargas/${cargaId}`);
        },
        onError: (e: Error) => toast({ title: "Não consegui registrar", description: e.message, variant: "destructive" }),
      },
    );
  }

  if (isLoading || !carga) {
    return <CompradorShell><PageSkeleton variante="painel" /></CompradorShell>;
  }

  return (
    <CompradorShell>
      {/* Folha estreita: é um formulário de uma coisa só, e largura cheia faria
          os campos atravessarem a tela inteira no desktop. */}
      <div className="mx-auto w-full max-w-[640px] space-y-4">
        <Button variant="ghost" size="sm" onClick={() => voltar(`/mercado/cargas/${cargaId}`)} className="-ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" />Voltar
        </Button>

        <div>
          <h1 className="text-xl md:text-2xl font-bold">Registrar produtor</h1>
          <p className="text-sm text-muted-foreground">
            Para quem não tem conta no Rizoma. Vai na carga {carga.identificador} como registro seu.
          </p>
        </div>

        {/* ── Produtor ── */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <div className="space-y-2">
            <Label htmlFor="produtor-nome">Produtor</Label>
            <Input
              id="produtor-nome" value={nome} autoComplete="off"
              onChange={e => { setNome(e.target.value); setContatoId(null); }}
              placeholder="Nome ou apelido" data-testid="input-produtor-nome"
            />
            {/* O autocomplete evita o segundo cadastro ANTES do 409 — é mais
                barato reconhecer do que responder a um diálogo. */}
            {!contatoId && (resolucao?.parecidos.length || resolucao?.exato) && (
              <ul className="rounded-lg border divide-y" data-testid="lista-sugestoes-produtor">
                {[resolucao.exato, ...resolucao.parecidos].filter(Boolean).slice(0, 4).map(p => (
                  <li key={p!.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover-elevate flex items-center justify-between gap-2"
                      onClick={() => { setContatoId(p!.id); setNome(p!.nome); }}
                      data-testid={`sugestao-${p!.id}`}
                    >
                      <span className="truncate">{p!.nome}{p!.cidade ? ` · ${p!.cidade}` : ""}</span>
                      {p!.noRizoma && <StatusChip kind="pago">no Rizoma</StatusChip>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!contatoId && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="produtor-telefone">WhatsApp</Label>
                <Input
                  id="produtor-telefone" inputMode="tel" value={telefone}
                  onChange={e => setTelefone(e.target.value)} placeholder="(74) 99999-8888"
                  data-testid="input-produtor-telefone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="produtor-cidade">Cidade</Label>
                <Input id="produtor-cidade" value={cidade} onChange={e => setCidade(e.target.value)} />
              </div>
            </div>
          )}
        </section>

        {/* ── O que carregou ── */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <h2 className="font-semibold">O que carregou</h2>

          <ul className="space-y-3">
            {linhas.map((linha, i) => (
              <li key={i} className="grid grid-cols-[1fr_auto] gap-2 items-end" data-testid={`linha-item-${i}`}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor={`produto-${i}`}>Produto</Label>
                    <Select
                      value={linha.produto}
                      onValueChange={v => setLinhas(ls => ls.map((l, j) => j === i ? { ...l, produto: v } : l))}
                    >
                      <SelectTrigger id={`produto-${i}`} data-testid={`select-produto-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRODUTOS.map(p => (
                          <SelectItem key={chaveProduto(p)} value={chaveProduto(p)}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor={`kg-${i}`}>kg</Label>
                    <Input
                      id={`kg-${i}`} inputMode="decimal" value={linha.kg}
                      onChange={e => setLinhas(ls => ls.map((l, j) => j === i ? { ...l, kg: e.target.value } : l))}
                      data-testid={`input-kg-${i}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor={`preco-${i}`}>R$/kg</Label>
                    <Input
                      id={`preco-${i}`} inputMode="decimal" value={linha.preco}
                      onChange={e => setLinhas(ls => ls.map((l, j) => j === i ? { ...l, preco: e.target.value } : l))}
                      data-testid={`input-preco-${i}`}
                    />
                  </div>
                </div>
                <Button
                  type="button" variant="ghost" size="icon"
                  onClick={() => setLinhas(ls => ls.length === 1 ? [{ ...LINHA_VAZIA }] : ls.filter((_, j) => j !== i))}
                  aria-label="Remover linha"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>

          <Button type="button" variant="outline" size="sm" onClick={() => setLinhas(ls => ls.concat([{ ...LINHA_VAZIA }]))} data-testid="button-mais-produto">
            <Plus className="h-4 w-4 mr-1" />Produto
          </Button>

          {itens.length > 0 && (
            <p className="text-sm tabular-nums pt-2 border-t">
              Total: <b>{fmtKgBR(totalKg)}</b> — <b>{fmtMoedaBR(totalValor)}</b>
            </p>
          )}

          {/* REFERÊNCIA, nunca preenchimento — ver o cabeçalho do arquivo. A
              praça do DIA DO CARREGAMENTO, não a de hoje. */}
          <CardPracaHoje dataVenda={carga?.dataCarregamento} />
        </section>

        {/* ── Vencimento ── */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <Label>Vencimento</Label>
          <div className="flex gap-2 flex-wrap">
            {ATALHOS_VENCIMENTO_ITEM.map(d => (
              <Button
                key={d} type="button" size="sm"
                variant={dias === d ? "default" : "outline"}
                onClick={() => { setDias(d); setDataLivre(""); }}
                disabled={!carga.dataCarregamento}
                data-testid={`chip-vencimento-${d}`}
              >
                {d} dias
              </Button>
            ))}
            <Input
              type="date" className="w-40" value={dataLivre}
              onChange={e => { setDataLivre(e.target.value); setDias(null); }}
              data-testid="input-vencimento-data"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {vencimento
              ? <>Vence em <b>{fmtDataBR(vencimento)}</b>{dias != null && " · contado do carregamento"}</>
              : carga.dataCarregamento
                ? "Sem vencimento — fica \"a definir\"."
                : "Esta carga não tem data de carregamento, então os atalhos não têm de onde contar."}
          </p>
        </section>

        {/* ── Prévia ── */}
        {itens.length > 0 && (
          <details className="rounded-lg border bg-card p-4">
            <summary className="cursor-pointer text-sm font-medium">
              Ver o que {nome.trim() || "o produtor"} vai receber
            </summary>
            <pre className="whitespace-pre-wrap text-sm font-sans mt-3 text-muted-foreground">
              {previa({ nome: nome.trim() || "Produtor", itens, totalKg, totalValor, vencimento })}
            </pre>
            {/* O LINK é do PR B: enquanto ele não existe, prometer "confira os
                números" seria mandar o produtor para uma página que não abre. */}
            <p className="text-xs text-muted-foreground mt-2">
              O link para o produtor conferir entra no próximo ciclo.
            </p>
          </details>
        )}

        <div className="flex gap-2 pb-4">
          <Button variant="outline" className="flex-1" onClick={() => voltar(`/mercado/cargas/${cargaId}`)}>
            Cancelar
          </Button>
          <Button
            className="flex-1" onClick={() => enviar(false)}
            disabled={!nome.trim() || itens.length === 0 || registrar.isPending}
            data-testid="button-salvar-registro"
          >
            {registrar.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </div>

      <DialogDuplicata
        parecidos={duplicata}
        nomeDigitado={nome.trim()}
        onEhEle={(id) => { setDuplicata(null); setContatoId(id); setNome(nome); enviarComContato(id); }}
        onEhOutro={() => { setDuplicata(null); enviar(true); }}
        onFechar={() => setDuplicata(null)}
      />
    </CompradorShell>
  );

  /** "É ele": reenvia amarrado ao contato existente, sem criar cadastro novo. */
  function enviarComContato(id: string) {
    registrar.mutate(
      { produtor: { contatoId: id }, itens, vencimento },
      {
        onSuccess: (r) => { if (r.ok) voltar(`/mercado/cargas/${cargaId}`); },
        onError: (e: Error) => toast({ title: "Não consegui registrar", description: e.message, variant: "destructive" }),
      },
    );
  }
}

/**
 * O diálogo de DUAS opções — "É ele" / "É outro".
 *
 * Duas e não três: "cancelar" já é o X do diálogo, e uma terceira via ("criar
 * mesmo assim, mas me pergunte de novo") não existe no mundo. A pergunta é curta
 * porque acontece no meio de digitar uma carga, na beira do caminhão.
 */
function DialogDuplicata({
  parecidos, nomeDigitado, onEhEle, onEhOutro, onFechar,
}: {
  parecidos: ProdutorCarteiraDTO[] | null;
  nomeDigitado: string;
  onEhEle: (id: string) => void;
  onEhOutro: () => void;
  onFechar: () => void;
}) {
  const primeiro = parecidos?.[0];
  return (
    <Dialog open={!!primeiro} onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Já existe alguém parecido</DialogTitle>
          <DialogDescription>
            {primeiro && (
              <><b>{primeiro.nome}</b>{primeiro.cidade ? ` (${primeiro.cidade})` : ""} já está na sua carteira.
                Você digitou <b>{nomeDigitado}</b>. É ele?</>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={onEhOutro} data-testid="button-e-outro">
            É outro
          </Button>
          <Button className="flex-1" onClick={() => primeiro && onEhEle(primeiro.id)} data-testid="button-e-ele">
            É ele
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A prévia do que o produtor vai ler. Espelha o texto do PR B (`mensagens.ts`)
 * na FORMA, não no byte: o texto de verdade é montado no servidor, e o dia em
 * que os dois divergirem é o dia em que a prévia mente.
 */
function previa(d: {
  nome: string;
  itens: ItemRegistrado[];
  totalKg: number;
  totalValor: number;
  vencimento: string | null;
}): string {
  const linhas = d.itens.map(i => {
    const rotulo = PRODUTOS.find(p => p.variedade === i.variedade && p.qualidade === i.qualidade)?.label
      ?? `${i.variedade} ${i.qualidade ?? ""}`.trim();
    return `• ${rotulo} — ${fmtKgBR(i.kg)} × ${fmtMoedaBR(i.precoKg)} — ${fmtMoedaBR(i.kg * i.precoKg)}`;
  });
  return [
    `${d.nome}`,
    "",
    ...linhas,
    "",
    `Total: ${fmtKgBR(d.totalKg)} — ${fmtMoedaBR(d.totalValor)}`,
    d.vencimento ? `Vencimento: ${fmtDataBR(d.vencimento)}` : "",
  ].filter(l => l !== undefined).join("\n").trim();
}
